import type { SchemaVersioned } from "./types";

// Every per-game record must carry an `id` and `finishedAt` so the store can
// look records up + sort newest-first without knowing the rest of the shape.
interface MinimalRecord extends SchemaVersioned {
  readonly id: string;
  readonly finishedAt: number;
}

export interface HistoryStoreOptions {
  readonly storageKey: string;
  readonly schemaVersion: number;
  // Game-specific shape check on each loaded record. The store already checks
  // schema version + presence of an object; this predicate asserts per-game
  // required fields (e.g. `Array.isArray(r.moves)` or `Array.isArray(r.turns)`).
  readonly isValid: (parsed: Record<string, unknown>) => boolean;
  // Defaults to 50 — the cap both apps shipped with. Override only if a future
  // game needs a different ceiling.
  readonly cap?: number;
}

// FIFO list of finished games, capped at `cap` (default 50). Newest first.
// On `setItem` quota errors the store drops oldest until the write fits.
export class HistoryStore<TRecord extends MinimalRecord> {
  private readonly opts: HistoryStoreOptions;
  private records: TRecord[];

  constructor(opts: HistoryStoreOptions) {
    this.opts = opts;
    this.records = this.load();
  }

  list(): readonly TRecord[] {
    return [...this.records].sort((a, b) => b.finishedAt - a.finishedAt);
  }

  get(id: string): TRecord | null {
    return this.records.find((r) => r.id === id) ?? null;
  }

  add(record: TRecord): void {
    this.records = [record, ...this.records];
    const cap = this.opts.cap ?? 50;
    if (this.records.length > cap) {
      this.records = this.records.slice(0, cap);
    }
    this.save();
  }

  delete(id: string): void {
    const before = this.records.length;
    this.records = this.records.filter((r) => r.id !== id);
    if (this.records.length !== before) {
      this.save();
    }
  }

  private isValid(value: unknown): value is TRecord {
    if (!value || typeof value !== "object") return false;
    const r = value as Partial<TRecord>;
    if (typeof r.id !== "string") return false;
    if (r.version !== this.opts.schemaVersion) return false;
    if (typeof r.finishedAt !== "number") return false;
    return this.opts.isValid(value as Record<string, unknown>);
  }

  private load(): TRecord[] {
    try {
      const raw = localStorage.getItem(this.opts.storageKey);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((v): v is TRecord => this.isValid(v));
    } catch {
      return [];
    }
  }

  private save(): void {
    try {
      localStorage.setItem(this.opts.storageKey, JSON.stringify(this.records));
    } catch {
      // Storage full — drop oldest until it fits, otherwise give up.
      while (this.records.length > 1) {
        this.records.pop();
        try {
          localStorage.setItem(this.opts.storageKey, JSON.stringify(this.records));
          return;
        } catch {
          // keep trimming
        }
      }
    }
  }
}
